import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Award, Zap, Target } from 'lucide-react';
import { ProofSection } from '../components/ProofSection';
import { Navigation } from '../components/Navigation';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const programs = [{
  title: 'Beginner Tae Kwon Do',
  description: 'Our Beginner Taekwondo students, 5 years old and older, learn the fundamentals of Tae Kwon Do. They learn basic kicks, blocks, and strikes. They will develop self-discipline, respect, and confidence.',
  icon: Users,
  imagePosition: 'right' as const
}, {
  title: 'Intermediate Taekwondo',
  description: 'Students in the Intermediate Taekwondo class will continue to be taught more advanced techniques of the basic fundamentals. They will learn more advanced kicking, blocking, and striking techniques. They will also be introduced to sparring and forms. They will continue to develop their confidence and discipline as they continue to train and test for their next belt rank.',
  icon: Target,
  imagePosition: 'left' as const
}, {
  title: 'Family Class',
  description: "Family class is great! Moms, Dads, and siblings. It is a great way to spend time together and learn something new. It's a great way to get in shape, develop self-discipline and increase your family bond. Try it, flexibility and confidence at the same time. Great for all relationships.",
  icon: Users,
  imagePosition: 'right' as const
}, {
  title: 'Black Belt & Bodan Program',
  description: 'Our Black Belt & Bodan program takes what is a solid commitment to our Taekwondo for our upper belts. It is a journey that starts at 1st Gup (red belt with black stripe) and continues through 1st Dan (1st degree black belt). Students in this program are developing their leadership skills, and are also learning to be a role model for our younger students. They are at the highest level of our program and are role models.',
  icon: Award,
  imagePosition: 'left' as const
}, {
  title: 'Hyper Pro Training',
  description: 'HPT (Hyper-pro-training) is a program that is for the elite athletes that have a strong desire to compete and win at the highest level. This program is designed to take the athletes to the next level. It is designed to develop speed, power, and technique. This program is for serious athletes.',
  icon: Zap,
  imagePosition: 'right' as const
}, {
  title: 'Juice Compound',
  description: "Juice Compound is a 30-45 minute class training program that focuses on will power to increase the athlete's endurance, strength, and speed. The program is designed to enhance their skills. The program is used for our competitive team as well as our students to increase their overall fitness level and mental toughness. It is a great way to train.",
  icon: Zap,
  imagePosition: 'left' as const
}];
export function ClassesPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-225">
      <Navigation data-id="element-226" />

      {/* Hero Section */}
      <section className="pt-16 pb-12 px-4 bg-gradient-to-b from-blue-50 to-white" data-id="element-227">
        <div className="max-w-5xl mx-auto text-center" data-id="element-228">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6" data-id="element-229">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-[#1e3a8a] mb-4" data-id="element-230">
              Our Programs
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-231">
              We offer programs for all ages and all skill levels, starting with
              our Jr. students in our Basic Tae Kwon Do Class, to our
              Competition Team. Join us today and see why we are the best choice
              for you and your family. Our schedule is flexible and our classes
              are small so you get the attention you need to succeed. Come join
              the family!
            </p>
            <div className="pt-4" data-id="element-232">
              <Link to="/" className="inline-block px-8 py-4 bg-[#dc2626] text-white text-lg font-bold rounded-lg shadow-lg hover:bg-red-700 transition-colors" data-id="element-233">
                Start Your $29 Trial
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 px-4" data-id="element-234">
        <div className="max-w-6xl mx-auto space-y-24" data-id="element-235">
          {programs.map((program, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center ${program.imagePosition === 'left' ? 'md:grid-flow-dense' : ''}`} data-id="element-236">
              {/* Text Content */}
              <div className={program.imagePosition === 'left' ? 'md:order-2' : ''} data-id="element-237">
                <div className="flex items-center gap-3 mb-4" data-id="element-238">
                  <div className="w-12 h-12 bg-[#1e3a8a] rounded-lg flex items-center justify-center" data-id="element-239">
                    <program.icon className="w-6 h-6 text-white" data-id="element-240" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black text-[#1e3a8a]" data-id="element-241">
                    {program.title}
                  </h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg" data-id="element-242">
                  {program.description}
                </p>
              </div>

              {/* Image Placeholder */}
              <div className={`${program.imagePosition === 'left' ? 'md:order-1' : ''}`} data-id="element-243">
                <div className="relative aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden shadow-lg" data-id="element-244">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-245">
                    <program.icon className="w-24 h-24 text-gray-300" data-id="element-246" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/20 to-transparent" data-id="element-247"></div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-248">
        <div className="max-w-4xl mx-auto text-center" data-id="element-249">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-250">
            <h2 className="text-3xl sm:text-4xl font-black text-[#1e3a8a] mb-6" data-id="element-251">
              Ready to Find Your Perfect Program?
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-252">
              Whether you're just starting out or training for competition, we
              have a program designed for your goals. Try any class for 2 weeks
              and see which one is right for you.
            </p>
            <Link to="/" className="inline-block px-8 py-4 bg-[#dc2626] text-white text-lg font-bold rounded-lg shadow-lg hover:bg-red-700 transition-colors" data-id="element-253">
              Start Your $29 Trial
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Proof Section */}
      <ProofSection testimonialIndex={2} data-id="element-254" />

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm" data-id="element-255">
        <div className="max-w-4xl mx-auto" data-id="element-256">
          <div className="font-black text-xl text-white mb-4 tracking-tighter" data-id="element-257">
            NWBBA
          </div>
          <p className="mb-6" data-id="element-258">Northwest Black Belt Academy</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8" data-id="element-259">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-260">
              Home
            </Link>
            <Link to="/about" className="hover:text-white transition-colors" data-id="element-261">
              About Us
            </Link>
            <Link to="/classes" className="text-white font-bold" data-id="element-262">
              Classes
            </Link>
            <Link to="/schedule" className="hover:text-white transition-colors" data-id="element-263">
              Schedule
            </Link>
            <Link to="/resources" className="hover:text-white transition-colors" data-id="element-264">
              Resources
            </Link>
            <Link to="/blog" className="hover:text-white transition-colors" data-id="element-265">
              Blog
            </Link>
            <Link to="/proof" className="hover:text-white transition-colors" data-id="element-266">
              Proof
            </Link>
            <a href="#" className="hover:text-white transition-colors" data-id="element-267">
              Contact
            </a>
          </div>
          <p data-id="element-268">
            &copy; {new Date().getFullYear()} Northwest Black Belt Academy. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}